import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/shared/PageLayout';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api-client';
import {
  RotateCcw,
  TrendingDown,
  TrendingUp,
  Filter,
  AlertTriangle,
  Lightbulb,
  CheckCircle2,
  XCircle,
  Activity,
  Layers,
  ArrowRight,
  ExternalLink,
  RefreshCw,
  Clock,
  Radio,
  Calendar,
  Camera,
  Image as ImageIcon,
  Maximize2,
} from 'lucide-react';
import { FullScreenImageDialog } from '@/components/ui/full-screen-image-dialog';

export interface DailyFunnelMetrics {
  date: string;
  initialTraffic: number;
  assessmentStarted: number;
  startedMobile?: number;
  startedDesktop?: number;
  pictureUploadPrompted: number;
  pictureSelected: number;
  pictureUploadFailed: number;
  pictureUploadSuccess: number;
  analysisSucceeded?: number;
  selfAssessmentCompleted: number;
  reportPdfDownloaded?: number;
  surveyOpened?: number;
  surveySubmitted?: number;
  bookingCalendarOpened?: number;
  walkthroughCallBooked: number;
  events: Record<string, number>;
  updatedAt?: string | null;
}

export interface ErroredPhotoSample {
  id: string;
  viewName: string;
  errorMessage: string;
  photoDataUrl: string;
  deviceType: 'mobile' | 'desktop';
  timestamp: string;
  userAgent?: string;
}

export interface RealFunnelMetrics {
  initialTraffic: number;
  assessmentStarted: number;
  startedMobile?: number;
  startedDesktop?: number;
  pictureUploadPrompted: number;
  pictureSelected: number;
  pictureUploadFailed: number;
  pictureUploadSuccess: number;
  analysisSucceeded?: number;
  selfAssessmentCompleted: number;
  reportPdfDownloaded?: number;
  surveyOpened?: number;
  surveySubmitted?: number;
  bookingCalendarOpened?: number;
  walkthroughCallBooked: number;
  totalDropOffs: number;
  overallConversionRate: number;
  lastEventAt: string | null;
  recentEvents: Array<{
    eventName: string;
    timestamp: string;
    viewName?: string;
    errorMessage?: string;
  }>;
  dailyBreakdown?: DailyFunnelMetrics[];
  erroredPhotoSamples?: ErroredPhotoSample[];
}

export function FunnelDropOffAnalyticsPage() {
  const { user: currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [metrics, setMetrics] = useState<RealFunnelMetrics | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  // Fetch real production metrics from protected admin API
  const fetchRealMetrics = useCallback(async (isSilent = false) => {
    if (!isSilent) setIsLoading(true);
    else setIsRefreshing(true);

    try {
      const data = await apiFetch<RealFunnelMetrics>('/admin/funnel-analytics', { cache: 'no-store' });
      setMetrics(data);
    } catch (err: any) {
      toast({
        title: 'Error loading analytics',
        description: err?.message || 'Failed to fetch production funnel data.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [toast]);

  // Initial fetch and 5s auto-polling loop
  useEffect(() => {
    if (currentUser) {
      fetchRealMetrics(false);
    }
  }, [currentUser, fetchRealMetrics]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchRealMetrics(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchRealMetrics]);

  // Auth gate check
  if (loading) {
    return (
      <PageLayout title="Funnel Drop-off Analytics">
        <div className="flex h-64 items-center justify-center">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Activity className="h-6 w-6 animate-spin text-primary" />
            <span>Connecting to real production stream...</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!currentUser) {
    return (
      <PageLayout title="Access Restricted">
        <Card className="mx-auto max-w-md mt-12 border-destructive/30">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Access Restricted
            </CardTitle>
            <CardDescription>
              Sales or admin access is required to view form progress.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  // Calculated stage conversion percentages from real production data
  const initialTraffic = metrics?.initialTraffic || 0;
  const started = metrics?.assessmentStarted || 0;
  const selected = metrics?.pictureSelected || 0;
  const success = metrics?.pictureUploadSuccess || 0;
  const analyzed = metrics?.analysisSucceeded || 0;
  const failed = metrics?.pictureUploadFailed || 0;
  const completed = metrics?.selfAssessmentCompleted || 0;
  const surveyOpened = metrics?.surveyOpened || 0;
  const surveySubmitted = metrics?.surveySubmitted || 0;
  const calendarOpened = metrics?.bookingCalendarOpened || 0;
  const booked = metrics?.walkthroughCallBooked || 0;

  const pdfDownloads = metrics?.reportPdfDownloaded || 0;
  const photoConvRate = initialTraffic > 0 ? ((success / initialTraffic) * 100).toFixed(1) : '0';
  const analyzedConvRate = started > 0 ? ((analyzed / started) * 100).toFixed(1) : '0';
  const submitConvRate = surveyOpened > 0 ? ((surveySubmitted / surveyOpened) * 100).toFixed(1) : '0';
  const surveyConvRate = analyzed > 0 ? ((surveyOpened / analyzed) * 100).toFixed(1) : '0';
  const pdfConvRate = analyzed > 0 ? ((pdfDownloads / analyzed) * 100).toFixed(1) : '0';
  const totalDropOffs = metrics?.totalDropOffs || Math.max(0, initialTraffic - completed);
  const overallConversion = metrics?.overallConversionRate ?? (initialTraffic > 0 ? ((completed / initialTraffic) * 100).toFixed(1) : 0);

  return (
    <PageLayout title="Funnel Drop-off Analytics">
      <div className="space-y-8 max-w-7xl mx-auto pb-12">
        {/* Header Title & Live Stream Status */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/40 pb-6">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-500">
                <Radio className="h-6 w-6 animate-pulse" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">Real Production Funnel Analytics</h1>
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30 font-semibold flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-ping" />
                Live Stream Active
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm mt-1.5">
              Real production user event telemetry aggregated live from self-assessment visits, picture uploads, landmark detections, and report completions.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <Button
              variant={autoRefresh ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAutoRefresh(prev => !prev)}
              className="gap-2 text-xs"
            >
              <Clock className="h-3.5 w-3.5" />
              <span>{autoRefresh ? 'Update list automatically: on' : 'Update list automatically: off'}</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRealMetrics(false)}
              disabled={isRefreshing || isLoading}
              className="gap-2 rounded-lg border-border/60 hover:bg-accent"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </Button>
          </div>
        </div>

        {/* Real Production Data Visual Funnel */}
        <Card className="border-border bg-card shadow-sm overflow-hidden rounded-2xl">
          <CardHeader className="border-b border-border bg-muted/30 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Layers className="h-5 w-5 text-emerald-500" /> Production User Flow & Conversion Stages
              </CardTitle>
              {metrics?.lastEventAt && (
                <span className="text-xs text-muted-foreground font-mono">
                  Last event: {new Date(metrics.lastEventAt).toLocaleTimeString()}
                </span>
              )}
            </div>
          </CardHeader>

          <CardContent className="p-6 md:p-8 space-y-8">
            {/* Diagram Flow Grid */}
            <div className="relative w-full rounded-xl bg-muted/20 border border-border p-6 md:p-8">
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 relative items-stretch">
                {/* Stage 1: Initial Traffic */}
                <div className="flex flex-col justify-between p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 relative">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider block">Stage 1</span>
                    <span className="text-sm font-bold text-foreground block">Page Views</span>
                  </div>
                  <div className="space-y-1 mt-4">
                    <div
                      data-testid="funnel-stage-page-views"
                      className="text-3xl font-black text-blue-600 dark:text-blue-400"
                    >
                      {initialTraffic}
                    </div>
                    <span className="text-xs text-muted-foreground">Visited /self-assessment</span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-1.5 bg-blue-500 rounded-b-xl" />
                </div>

                {/* Stage 2: Started Assessment (uploaded photos) */}
                <div className="flex flex-col justify-between p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 relative">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider block">Stage 2</span>
                    <span className="text-sm font-bold text-foreground block">Photos Uploaded</span>
                  </div>
                  <div className="space-y-1 mt-4">
                    <div
                      data-testid="funnel-stage-photos-uploaded"
                      className="text-3xl font-black text-emerald-600 dark:text-emerald-400"
                    >
                      {success}
                    </div>
                    <div className="text-xs space-y-0.5">
                      <span className="text-emerald-700 dark:text-emerald-300 font-medium block">{photoConvRate}% of visitors</span>
                      {failed > 0 && <span className="text-destructive font-bold">{failed} failed</span>}
                    </div>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-1.5 bg-emerald-500 rounded-b-xl" />
                </div>

                {/* Stage 3: Full analysis succeeded */}
                <div className="flex flex-col justify-between p-4 rounded-xl bg-lime-500/10 border border-lime-500/30 relative">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-lime-700 dark:text-lime-400 uppercase tracking-wider block">Stage 3</span>
                    <span className="text-sm font-bold text-foreground block">Analyzed</span>
                  </div>
                  <div className="space-y-1 mt-4">
                    <div
                      data-testid="funnel-stage-analyzed"
                      className="text-3xl font-black text-lime-700 dark:text-lime-400"
                    >
                      {analyzed}
                    </div>
                    <span className="text-xs text-lime-800 dark:text-lime-300 font-medium">{analyzedConvRate}% of starts</span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-1.5 bg-lime-500 rounded-b-xl" />
                </div>

                {/* Stage 4: Survey Opened */}
                <div className="flex flex-col justify-between p-4 rounded-xl bg-violet-500/10 border border-violet-500/30 relative">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider block">Stage 4</span>
                    <span className="text-sm font-bold text-foreground block">Survey Opened</span>
                  </div>
                  <div className="space-y-1 mt-4">
                    <div
                      data-testid="funnel-stage-survey-opened"
                      className="text-3xl font-black text-violet-600 dark:text-violet-400"
                    >
                      {surveyOpened}
                    </div>
                    <span className="text-xs text-violet-700 dark:text-violet-300 font-medium">{surveyConvRate}% of completions</span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-1.5 bg-violet-500 rounded-b-xl" />
                </div>

                {/* Stage 5: GHL survey submitted */}
                <div className="flex flex-col justify-between p-4 rounded-xl bg-teal-500/10 border border-teal-500/30 relative">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-teal-600 dark:text-teal-400 uppercase tracking-wider block">Stage 5</span>
                    <span className="text-sm font-bold text-foreground block">Survey Submitted</span>
                  </div>
                  <div className="space-y-1 mt-4">
                    <div
                      data-testid="funnel-stage-survey-submitted"
                      className="text-3xl font-black text-teal-600 dark:text-teal-400"
                    >
                      {surveySubmitted}
                    </div>
                    <span className="text-xs text-teal-700 dark:text-teal-300 font-medium">{submitConvRate}% of survey opens</span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-1.5 bg-teal-500 rounded-b-xl" />
                </div>

                {/* Stage 6: PDF downloaded — the ending after the survey */}
                <div className="flex flex-col justify-between p-4 rounded-xl bg-purple-500/10 border border-purple-500/30 relative">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider block">Stage 6</span>
                    <span className="text-sm font-bold text-foreground block">PDF Downloaded</span>
                  </div>
                  <div className="space-y-1 mt-4">
                    <div
                      data-testid="funnel-stage-pdf-downloads"
                      className="text-3xl font-black text-purple-600 dark:text-purple-400"
                    >
                      {pdfDownloads}
                    </div>
                    <span className="text-xs text-purple-700 dark:text-purple-300 font-medium">{pdfConvRate}% of analyzed</span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-1.5 bg-purple-500 rounded-b-xl" />
                </div>
              </div>

              {/* Real Total Drop-offs Ribbon */}
              <div className="mt-4 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-between">
                <div className="flex items-center gap-2.5 text-purple-700 dark:text-purple-300">
                  <TrendingDown className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <span className="text-xs font-medium">Real Production Accumulated Drop-offs</span>
                </div>
                <div className="text-sm font-bold text-purple-700 dark:text-purple-300">
                  {totalDropOffs} Users ({initialTraffic > 0 ? ((totalDropOffs / initialTraffic) * 100).toFixed(1) : 0}% Drop Rate)
                </div>
              </div>
            </div>

            {/* Extra KPIs that are not already a funnel stage */}
              <div data-testid="funnel-extra-kpis" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 pt-2">
                <div className="p-4 rounded-xl bg-card border border-border text-center space-y-1 shadow-sm">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Calendar Opened</span>
                  <div
                    data-testid="funnel-kpi-calendar-opened"
                    className="text-2xl font-black text-amber-600 dark:text-amber-400 tracking-tight"
                  >
                    {calendarOpened}
                  </div>
                  <span className="text-xs text-muted-foreground">Booking calendar</span>
                </div>

                <div className="p-4 rounded-xl bg-card border border-border text-center space-y-1 shadow-sm">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Appointments Booked</span>
                  <div
                    data-testid="funnel-kpi-booked"
                    className="text-2xl font-black text-cyan-600 dark:text-cyan-400 tracking-tight"
                  >
                    {booked}
                  </div>
                  <span className="text-xs text-muted-foreground">Walkthrough calls</span>
                </div>

                <div className="p-4 rounded-xl bg-card border border-border text-center space-y-1 shadow-sm">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Device Split</span>
                  <div className="text-lg font-black text-indigo-600 dark:text-indigo-400 tracking-tight flex items-center justify-center gap-1.5 mt-1">
                    <span>📱 {metrics?.startedMobile || 0}</span>
                    <span className="text-muted-foreground/50">/</span>
                    <span>💻 {metrics?.startedDesktop || 0}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">Mobile / Desktop</span>
                </div>

                <div className="p-4 rounded-xl bg-card border border-border text-center space-y-1 shadow-sm">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Total Drop-offs</span>
                  <div className="text-2xl font-black text-rose-600 dark:text-rose-400 tracking-tight">{totalDropOffs}</div>
                  <span className="text-xs text-muted-foreground">Users Abandoned</span>
                </div>

                <div className="p-4 rounded-xl bg-card border border-border text-center space-y-1 shadow-sm">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Overall Conversion</span>
                  <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">{overallConversion}%</div>
                  <span className="text-xs text-muted-foreground">End-to-End</span>
                </div>
              </div>
          </CardContent>
        </Card>

        {/* Daily Breakdown (Counts per day) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-indigo-500" /> Daily Funnel Telemetry Breakdown (Counts Per Day)
              </CardTitle>
              <CardDescription>
                Historical aggregated daily event volume and stage conversions.
              </CardDescription>
            </div>
            <Badge variant="outline" className="bg-indigo-500/10 text-indigo-500 border-indigo-500/20 font-mono text-xs">
              {metrics?.dailyBreakdown?.length || 0} Days Tracked
            </Badge>
          </CardHeader>

          <CardContent>
            {metrics?.dailyBreakdown && metrics.dailyBreakdown.length > 0 ? (
              <div className="divide-y divide-border rounded-lg border border-border overflow-x-auto text-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-muted/50 text-xs font-semibold text-muted-foreground border-b border-border">
                      <th className="p-3">Date</th>
                      <th className="p-3">Traffic</th>
                      <th className="p-3">Photos OK</th>
                      <th className="p-3 text-lime-600" title="Users who uploaded both required photos and received a live classification">Analyzed</th>
                      <th className="p-3 text-violet-500" title="Users who opened the GHL survey modal">Survey Opened</th>
                      <th className="p-3 text-teal-500" title="Users who submitted the GHL booking survey">Survey Submitted</th>
                      <th className="p-3 text-amber-500" title="Users who opened the booking calendar">Calendar Opened</th>
                      <th className="p-3 text-cyan-500" title="Appointments actually booked">Booked</th>
                      <th className="p-3 text-purple-500" title="Prospects who downloaded/exported the PDF report">PDF Downloads</th>
                      <th className="p-3">Top Events</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {metrics.dailyBreakdown.map((day) => {
                      const topEventsStr = Object.entries(day.events || {})
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 3)
                        .map(([evt, count]) => `${evt}: ${count}`)
                        .join(', ');

                      return (
                        <tr
                          key={day.date}
                          data-testid={`funnel-daily-row-${day.date}`}
                          className="hover:bg-muted/30 transition-colors text-xs font-mono"
                        >
                          <td className="p-3 font-bold text-foreground">{day.date}</td>
                          <td data-col="traffic" className="p-3 text-blue-500 font-bold">{day.initialTraffic}</td>
                          <td data-col="photos" className="p-3">
                            <span className="text-green-500 font-bold">{day.pictureUploadSuccess}</span>
                            {day.pictureUploadFailed > 0 && (
                              <button
                                type="button"
                                onClick={() => document.getElementById('errored-photos-section')?.scrollIntoView({ behavior: 'smooth' })}
                                className="text-rose-500 hover:text-rose-400 font-semibold underline underline-offset-2 cursor-pointer ml-1 text-xs"
                                title="Jump to Errored Photo Samples"
                              >
                                ({day.pictureUploadFailed} err)
                              </button>
                            )}
                          </td>
                          <td data-col="analyzed" className="p-3 text-lime-600 font-bold">{day.analysisSucceeded || 0}</td>
                          <td data-col="survey-opened" className="p-3 text-violet-500 font-bold">{day.surveyOpened || 0}</td>
                          <td data-col="survey-submitted" className="p-3 text-teal-500 font-bold">{day.surveySubmitted || 0}</td>
                          <td data-col="calendar-opened" className="p-3 text-amber-500 font-bold">{day.bookingCalendarOpened || 0}</td>
                          <td data-col="booked" className="p-3 text-cyan-500 font-bold">{day.walkthroughCallBooked}</td>
                          <td data-col="pdf" className="p-3 text-purple-500 font-bold">{day.reportPdfDownloaded || 0}</td>
                          <td className="p-3 text-muted-foreground text-[11px] font-sans truncate max-w-xs" title={topEventsStr}>
                            {topEventsStr || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
                Daily telemetry data will populate automatically as users visit and submit assessments.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Errored Photo Samples Gallery */}
        <Card id="errored-photos-section" className="border-rose-500/20 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2 text-rose-400">
                <Camera className="h-5 w-5 text-rose-500" /> Errored Photo Samples (Funnel Drop Off Inspection)
              </CardTitle>
              <CardDescription>
                Downsampled photo samples captured when landmark detection or photo processing fails during user self-assessment.
              </CardDescription>
            </div>
            <Badge variant="outline" className="bg-rose-500/10 text-rose-400 border-rose-500/20 font-mono text-xs">
              {metrics?.erroredPhotoSamples?.length || 0} Saved Samples
            </Badge>
          </CardHeader>

          <CardContent>
            {metrics?.erroredPhotoSamples && metrics.erroredPhotoSamples.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {metrics.erroredPhotoSamples.map((sample) => (
                  <div
                    key={sample.id}
                    className="flex flex-col bg-card rounded-xl border border-border overflow-hidden shadow-sm hover:border-border/80 transition-all"
                  >
                    {/* Thumbnail with FullScreenImageDialog Lightbox */}
                    <div className="relative aspect-[3/4] bg-muted/30 flex items-center justify-center group overflow-hidden">
                      {sample.photoDataUrl ? (
                        <FullScreenImageDialog
                          imageUrl={sample.photoDataUrl}
                          altText={`Errored photo sample - ${sample.viewName}`}
                        >
                          <div className="relative w-full h-full cursor-pointer">
                            <img
                              src={sample.photoDataUrl}
                              alt={`Error in ${sample.viewName} view`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 text-white text-xs font-semibold">
                              <Maximize2 className="h-4 w-4" /> Click to Enlarge
                            </div>
                          </div>
                        </FullScreenImageDialog>
                      ) : (
                        <div className="flex flex-col items-center justify-center p-4 text-center text-muted-foreground text-xs">
                          <ImageIcon className="h-8 w-8 mb-1 opacity-50" />
                          <span>No Image Preview</span>
                        </div>
                      )}

                      {/* View Badge */}
                      <Badge className="absolute top-2 left-2 bg-background/90 backdrop-blur text-foreground border-border text-[10px] font-semibold">
                        {sample.viewName}
                      </Badge>

                      {/* Device Indicator */}
                      <Badge variant="outline" className="absolute top-2 right-2 bg-background/90 backdrop-blur text-muted-foreground border-border text-[10px]">
                        {sample.deviceType === 'mobile' ? '📱 Mobile' : '💻 Desktop'}
                      </Badge>
                    </div>

                    {/* Metadata & Error message */}
                    <div className="p-3 flex-1 flex flex-col justify-between space-y-2">
                      <div className="p-2 rounded bg-destructive/10 border border-destructive/20 text-destructive text-xs font-sans break-words leading-tight">
                        <span className="font-semibold block text-[10px] uppercase tracking-wider mb-0.5">Error</span>
                        {sample.errorMessage || 'Photo analysis error'}
                      </div>

                      <div className="text-[11px] text-muted-foreground font-mono flex items-center justify-between pt-1 border-t border-border">
                        <span>{new Date(sample.timestamp).toLocaleDateString()}</span>
                        <span>{new Date(sample.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
                No errored photo samples recorded yet. When photo uploads or posture landmark processing fail in self-assessment mode, downscaled samples will appear here for inspection.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Live Event Stream Feed */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5 text-emerald-500" /> Recent Live Event Stream
              </CardTitle>
              <CardDescription>
                Real-time user actions recorded by the self-assessment funnel tracker.
              </CardDescription>
            </div>
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 font-mono text-xs">
              {metrics?.recentEvents?.length || 0} Recent Events
            </Badge>
          </CardHeader>

          <CardContent>
            {metrics?.recentEvents && metrics.recentEvents.length > 0 ? (
              <div className="divide-y divide-border rounded-lg border border-border overflow-hidden text-sm">
                <div className="grid grid-cols-12 bg-muted/50 p-3 font-semibold text-xs text-muted-foreground">
                  <div className="col-span-3">Timestamp</div>
                  <div className="col-span-4">Event Name</div>
                  <div className="col-span-3">View / Step</div>
                  <div className="col-span-2 text-right">Status</div>
                </div>

                {metrics.recentEvents.map((evt, idx) => {
                  const isError = evt.eventName.includes('failed') || evt.eventName.includes('error');
                  return (
                    <div key={idx} className="grid grid-cols-12 p-3 items-center hover:bg-muted/30 transition-colors">
                      <div className="col-span-3 text-xs text-muted-foreground font-mono">
                        {new Date(evt.timestamp).toLocaleTimeString()}
                      </div>
                      <div className="col-span-4 font-mono text-xs font-bold text-foreground">
                        {evt.eventName}
                      </div>
                      <div className="col-span-3 text-xs text-muted-foreground">
                        {evt.viewName || '-'}
                      </div>
                      <div className="col-span-2 text-right">
                        {isError ? (
                          <Badge variant="destructive" className="text-[10px]">Error</Badge>
                        ) : (
                          <Badge variant="default" className="text-[10px] bg-emerald-600">Logged</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
                No production events recorded yet. Navigate to <code className="text-xs bg-muted px-1.5 py-0.5 rounded text-foreground font-mono">/self-assessment</code> to generate real event streams.
              </div>
            )}

            {/* Link to Firebase Console */}
            <div className="mt-6 p-4 rounded-lg bg-secondary/50 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                <span>
                  View long-term aggregated production funnels in <strong>Firebase Console → Analytics → Funnel Exploration</strong>.
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs font-semibold hover:bg-accent"
                onClick={() => window.open('https://console.firebase.google.com/', '_blank')}
              >
                <span>Open Firebase Console</span>
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
